import React, { useRef, useState, useEffect, useCallback } from 'react';
import { apiHelpers, apiService } from '../services/api';

const DocumentUpload = ({ clientId, onUploadSuccess }) => {
  // Local state - component owns its upload state
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Toggle between file upload and text input
  const [mode, setMode] = useState('file'); // 'file' or 'text'

  // Text input state
  const [textContent, setTextContent] = useState('');
  const [sourceTable, setSourceTable] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [sourceTimestamp, setSourceTimestamp] = useState('');

  // Refs for stable references that survive re-renders
  const fileInputRef = useRef(null);
  const uploadHandlerRef = useRef(null);
  const textUploadHandlerRef = useRef(null);

  // Create stable upload handler using ref
  // This handler reference never changes, preventing stale closures
  useEffect(() => {
    uploadHandlerRef.current = async (file) => {
      if (!clientId || !file) {
        console.error('Missing clientId or file');
        return;
      }

      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        // Direct API call - no hook intermediary
        const result = await apiHelpers.uploadAndWaitForCompletion(
          clientId,
          file,
          {},
          (progressPercent) => {
            setProgress(progressPercent);
          }
        );

        if (result.status === 'completed') {
          setProgress(100);

          // Notify parent with just the document ID
          if (onUploadSuccess) {
            onUploadSuccess(result.document_id);
          }

          // Clear state after brief delay
          setTimeout(() => {
            setProgress(0);
            setUploading(false);
            setError(null);
          }, 1500);
        } else {
          throw new Error(result.error || 'Upload failed');
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.message || 'Upload failed');
        setUploading(false);

        // Clear error after 5 seconds
        setTimeout(() => {
          setError(null);
        }, 5000);
      }
    };
  }, [clientId, onUploadSuccess]);

  // Create stable text upload handler using ref
  useEffect(() => {
    textUploadHandlerRef.current = async () => {
      if (!clientId || !textContent.trim()) {
        console.error('Missing clientId or text content');
        return;
      }

      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        // Convert datetime-local to ISO 8601 / RFC 3339 format if provided
        const timestampISO = sourceTimestamp
          ? new Date(sourceTimestamp).toISOString()
          : null;

        // Call text ingestion API
        const result = await apiService.ingestText(
          clientId,
          textContent.trim(),
          sourceTable.trim() || 'manual_entry',
          sourceId.trim() || `text_${Date.now()}`,
          timestampISO,
          {}
        );

        if (result.success) {
          setProgress(100);

          // Notify parent with the document ID
          if (onUploadSuccess) {
            onUploadSuccess(result.document_id);
          }

          // Clear form after brief delay
          setTimeout(() => {
            setTextContent('');
            setSourceTable('');
            setSourceId('');
            setSourceTimestamp('');
            setProgress(0);
            setUploading(false);
            setError(null);
          }, 1500);
        } else {
          throw new Error(result.error || 'Text ingestion failed');
        }
      } catch (err) {
        console.error('Text ingestion error:', err);
        setError(err.message || 'Text ingestion failed');
        setUploading(false);

        // Clear error after 5 seconds
        setTimeout(() => {
          setError(null);
        }, 5000);
      }
    };
  }, [clientId, textContent, sourceTable, sourceId, sourceTimestamp, onUploadSuccess]);

  // File selection handler - always stable (no dependencies)
  const handleFileSelect = useCallback((files) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate file type
    if (!file.type.includes('pdf')) {
      setError('Please select a PDF file');
      setTimeout(() => setError(null), 3000);

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size must be less than 50MB');
      setTimeout(() => setError(null), 3000);

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Call stable upload handler via ref
    if (uploadHandlerRef.current) {
      uploadHandlerRef.current(file);
    }

    // Clear the file input immediately for re-upload capability
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []); // No dependencies - always stable

  const handleClick = useCallback(() => {
    if (!clientId) {
      setError('Please select a client first');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (uploading) {
      return; // Don't allow new uploads while one is in progress
    }

    fileInputRef.current?.click();
  }, [clientId, uploading]);

  const handleFileChange = useCallback((e) => {
    handleFileSelect(e.target.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!dragOver && clientId && !uploading) {
      setDragOver(true);
    }
  }, [dragOver, clientId, uploading]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);

    if (!clientId) {
      setError('Please select a client first');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (uploading) {
      return;
    }

    handleFileSelect(e.dataTransfer.files);
  }, [clientId, uploading, handleFileSelect]);

  const handleTextSubmit = useCallback(() => {
    if (!clientId) {
      setError('Please select a client first');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (!textContent.trim()) {
      setError('Please enter some text');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (uploading) {
      return;
    }

    if (textUploadHandlerRef.current) {
      textUploadHandlerRef.current();
    }
  }, [clientId, textContent, uploading]);

  return (
    <div className="document-upload">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0 }}>Add Content</h3>
        {/* Mode Toggle */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          background: '#f8f9fa',
          padding: '0.25rem',
          borderRadius: '6px',
          border: '1px solid #e1e4e8'
        }}>
          <button
            onClick={() => setMode('file')}
            disabled={uploading}
            style={{
              padding: '0.4rem 0.8rem',
              background: mode === 'file' ? '#0d9488' : 'transparent',
              color: mode === 'file' ? 'white' : '#64748b',
              border: 'none',
              borderRadius: '4px',
              cursor: uploading ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem',
              fontWeight: '500',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            File
          </button>
          <button
            onClick={() => setMode('text')}
            disabled={uploading}
            style={{
              padding: '0.4rem 0.8rem',
              background: mode === 'text' ? '#0d9488' : 'transparent',
              color: mode === 'text' ? 'white' : '#64748b',
              border: 'none',
              borderRadius: '4px',
              cursor: uploading ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem',
              fontWeight: '500',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <line x1="10" y1="9" x2="8" y2="9"></line>
            </svg>
            Text
          </button>
        </div>
      </div>

      {!clientId && (
        <div className="error">
          Please select a client before adding content
        </div>
      )}

      {/* File Upload Mode */}
      {mode === 'file' && (
        <div
          className={`upload-area ${dragOver ? 'dragover' : ''} ${!clientId ? 'disabled' : ''}`}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            opacity: !clientId || uploading ? 0.5 : 1,
            cursor: !clientId || uploading ? 'not-allowed' : 'pointer'
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="file-input"
            accept=".pdf"
            onChange={handleFileChange}
            disabled={!clientId || uploading}
            style={{ display: 'none' }}
          />

          <div className="upload-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
          </div>

          <div className="upload-text">
            {uploading ? (
              <>
                <div>Processing...</div>
                <div style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  Please wait while your document is processed
                </div>
              </>
            ) : (
              <>
                <div>Click to select PDF or drag and drop</div>
                <div style={{ fontSize: '0.8rem', marginTop: '0.5rem', color: '#64748b' }}>
                  Supports PDF files up to 50MB
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Text Input Mode */}
      {mode === 'text' && (
        <div style={{
          border: '2px dashed #d1d5db',
          borderRadius: '8px',
          padding: '1rem',
          background: !clientId || uploading ? '#f9fafb' : 'white'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: '600',
              marginBottom: '0.5rem',
              color: '#374151'
            }}>
              Text Content *
            </label>
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              disabled={!clientId || uploading}
              placeholder="Paste your text content here (e.g., patient notes, lab results, medication orders)..."
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontFamily: 'inherit',
                resize: 'vertical',
                opacity: !clientId || uploading ? 0.5 : 1
              }}
            />
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.8rem',
                fontWeight: '600',
                marginBottom: '0.4rem',
                color: '#374151'
              }}>
                Source Table (optional)
              </label>
              <input
                type="text"
                value={sourceTable}
                onChange={(e) => setSourceTable(e.target.value)}
                disabled={!clientId || uploading}
                placeholder="e.g., medications, lab_results"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  opacity: !clientId || uploading ? 0.5 : 1
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '0.8rem',
                fontWeight: '600',
                marginBottom: '0.4rem',
                color: '#374151'
              }}>
                Source ID (optional)
              </label>
              <input
                type="text"
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
                disabled={!clientId || uploading}
                placeholder="e.g., MED-001, LAB-2024-123"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  opacity: !clientId || uploading ? 0.5 : 1
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.8rem',
              fontWeight: '600',
              marginBottom: '0.4rem',
              color: '#374151'
            }}>
              Source Timestamp (optional)
            </label>
            <input
              type="datetime-local"
              value={sourceTimestamp}
              onChange={(e) => setSourceTimestamp(e.target.value)}
              disabled={!clientId || uploading}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.85rem',
                opacity: !clientId || uploading ? 0.5 : 1
              }}
            />
            <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.3rem' }}>
              When did this record/event occur? (enables time-based queries)
            </div>
          </div>

          <button
            onClick={handleTextSubmit}
            disabled={!clientId || !textContent.trim() || uploading}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: !clientId || !textContent.trim() || uploading ? '#cbd5e1' : '#0d9488',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.85rem',
              fontWeight: '500',
              cursor: !clientId || !textContent.trim() || uploading ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            {uploading ? 'Processing...' : 'Submit Text'}
          </button>
        </div>
      )}

      {uploading && (
        <div className="upload-progress">
          <div
            className="progress-bar"
            style={{ width: `${progress}%` }}
          ></div>
          <div className="progress-text">
            {mode === 'file'
              ? (progress < 100 ? `Uploading: ${progress}%` : 'Processing document...')
              : 'Processing text...'
            }
          </div>
        </div>
      )}

      {error && (
        <div className="error">
          {error}
        </div>
      )}

      {mode === 'file' && (
        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '1rem', lineHeight: '1.5' }}>
          <strong>Supported formats:</strong> PDF<br />
          <strong>File size limit:</strong> 50MB<br />
          <strong>Processing time:</strong> Varies by document size
        </div>
      )}

      {mode === 'text' && (
        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '1rem', lineHeight: '1.5' }}>
          <strong>Tips:</strong><br />
          Use Source Timestamp to preserve the original record date<br />
          Source Table and ID help track where the data came from<br />
          Text is chunked automatically for semantic search
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
